/**
 * ProseMirror schema for the unified Editor component.
 *
 * This is a minimal schema designed to support the document structure used
 * in Markeroo. It's intentionally simple to match what TipTap's StarterKit
 * provides when most formatting is disabled.
 *
 * Based on: ai_plans/standalone-lift/src/editor/schema.ts
 *
 * Node types:
 * - doc: Root document node
 * - paragraph: Block-level text container with blockKey attribute
 * - heading: Block-level heading (h1, h2, h3) with blockKey attribute
 * - bullet_list: Unordered list container
 * - ordered_list: Numbered list container
 * - list_item: Individual list item
 * - blockquote: Block-level quote container with blockKey attribute
 * - code_block: Block-level code with monospace font, no inline formatting
 * - text: Inline text content
 * - hardBreak: Line break within a paragraph (<br>)
 *
 * Marks (inline formatting):
 * - strong: Bold text
 * - em: Italic text
 * - underline: Underlined text
 * - code: Inline code
 * - link: Hyperlinks (with href and title)
 */

import { Schema } from "@tiptap/pm/model";

export const schema = new Schema({
  nodes: {
    doc: {
      content: "block+",
    },

    paragraph: {
      content: "inline*",
      group: "block",
      attrs: {
        blockKey: { default: "" },
      },
      parseDOM: [
        {
          tag: "p",
          getAttrs: (dom: HTMLElement) => ({
            blockKey: dom.getAttribute("data-block-key") || "",
          }),
        },
      ],
      toDOM(node) {
        const { blockKey } = node.attrs as { blockKey: string };
        return blockKey
          ? ["p", { "data-block-key": blockKey }, 0]
          : ["p", 0];
      },
    },

    heading: {
      content: "inline*",
      group: "block",
      attrs: {
        level: { default: 1 },
        blockKey: { default: "" },
      },
      parseDOM: [
        {
          tag: "h1",
          getAttrs: (dom: HTMLElement) => ({
            level: 1,
            blockKey: dom.getAttribute("data-block-key") || "",
          }),
        },
        {
          tag: "h2",
          getAttrs: (dom: HTMLElement) => ({
            level: 2,
            blockKey: dom.getAttribute("data-block-key") || "",
          }),
        },
        {
          tag: "h3",
          getAttrs: (dom: HTMLElement) => ({
            level: 3,
            blockKey: dom.getAttribute("data-block-key") || "",
          }),
        },
      ],
      toDOM(node) {
        const { level, blockKey } = node.attrs as {
          level: number;
          blockKey: string;
        };
        // Clamp level to 1-3 range
        const safeLevel = Math.min(3, Math.max(1, level));
        const tag = `h${safeLevel}` as "h1" | "h2" | "h3";
        return blockKey
          ? [tag, { "data-block-key": blockKey }, 0]
          : [tag, 0];
      },
    },

    text: {
      group: "inline",
    },

    hardBreak: {
      inline: true,
      group: "inline",
      selectable: false,
      parseDOM: [{ tag: "br" }],
      toDOM() {
        return ["br"];
      },
      leafText: () => "\n",
    },

    bullet_list: {
      content: "list_item+",
      group: "block",
      parseDOM: [{ tag: "ul" }],
      toDOM() {
        return ["ul", 0];
      },
    },

    ordered_list: {
      content: "list_item+",
      group: "block",
      attrs: {
        order: { default: 1 },
      },
      parseDOM: [
        {
          tag: "ol",
          getAttrs(dom: HTMLElement) {
            return {
              order: dom.hasAttribute("start")
                ? parseInt(dom.getAttribute("start") ?? "1", 10)
                : 1,
            };
          },
        },
      ],
      toDOM(node) {
        const order = (node.attrs as { order: number }).order;
        return order === 1 ? ["ol", 0] : ["ol", { start: order }, 0];
      },
    },

    list_item: {
      content: "paragraph block*",
      parseDOM: [{ tag: "li" }],
      toDOM() {
        return ["li", 0];
      },
      defining: true,
    },

    blockquote: {
      content: "block+",
      group: "block",
      attrs: {
        blockKey: { default: "" },
      },
      parseDOM: [
        {
          tag: "blockquote",
          getAttrs: (dom: HTMLElement) => ({
            blockKey: dom.getAttribute("data-block-key") || "",
          }),
        },
      ],
      toDOM(node) {
        const { blockKey } = node.attrs as { blockKey: string };
        return blockKey
          ? ["blockquote", { "data-block-key": blockKey }, 0]
          : ["blockquote", 0];
      },
    },

    code_block: {
      content: "text*",
      group: "block",
      code: true,
      defining: true,
      attrs: {
        blockKey: { default: "" },
      },
      parseDOM: [
        {
          tag: "pre",
          preserveWhitespace: "full" as const,
          getAttrs: (dom: HTMLElement) => ({
            blockKey: dom.getAttribute("data-block-key") || "",
          }),
        },
      ],
      toDOM(node) {
        const { blockKey } = node.attrs as { blockKey: string };
        const attrs = blockKey ? { "data-block-key": blockKey } : {};
        return ["pre", attrs, ["code", 0]];
      },
    },
  },

  marks: {
    strong: {
      parseDOM: [
        { tag: "strong" },
        { tag: "b", getAttrs: (node: HTMLElement) => node.style.fontWeight !== "normal" && null },
        { style: "font-weight=400", clearMark: (m) => m.type.name === "strong" },
        {
          style: "font-weight",
          getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null,
        },
      ],
      toDOM() {
        return ["strong", 0];
      },
    },

    em: {
      parseDOM: [
        { tag: "i" },
        { tag: "em" },
        { style: "font-style=italic" },
        { style: "font-style=normal", clearMark: (m) => m.type.name === "em" },
      ],
      toDOM() {
        return ["em", 0];
      },
    },

    underline: {
      parseDOM: [
        { tag: "u" },
        {
          style: "text-decoration",
          getAttrs: (value: string) => value.includes("underline") && null,
        },
      ],
      toDOM() {
        return ["u", 0];
      },
    },

    code: {
      parseDOM: [{ tag: "code" }],
      toDOM() {
        return ["code", 0];
      },
    },

    link: {
      attrs: {
        href: {},
        title: { default: null },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: "a[href]",
          getAttrs(dom: HTMLElement) {
            return {
              href: dom.getAttribute("href"),
              title: dom.getAttribute("title"),
            };
          },
        },
      ],
      toDOM(node) {
        const { href, title } = node.attrs as { href: string; title: string | null };
        const attrs: { href: string; title?: string } = { href };
        if (title) {
          attrs.title = title;
        }
        return ["a", attrs, 0];
      },
    },
  },
});

/**
 * Create an empty document with a single empty paragraph.
 * Useful for initializing the editor when no content is provided.
 */
export function createEmptyDoc() {
  return schema.node("doc", null, [schema.node("paragraph")]);
}

/**
 * Create a document from plain text, splitting on double newlines for paragraphs.
 */
export function createDocFromText(text: string) {
  const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());

  if (paragraphs.length === 0) {
    return createEmptyDoc();
  }

  const nodes = paragraphs.map((p) => {
    const trimmed = p.trim();
    if (!trimmed) {
      return schema.node("paragraph");
    }
    return schema.node("paragraph", null, [schema.text(trimmed)]);
  });

  return schema.node("doc", null, nodes);
}
